/**
 * Reusable ring-burst FX (R2-M4 VFX list): landing impact rings, checkpoint
 * activation double-rings + a persistent lit slab behind the player, and the
 * on-beat pulse ring around the ship. A fixed pool of 4 torus meshes is
 * allocated once and recycled — zero per-event allocation, zero when idle.
 * Every effect is a single sub-3 Hz event (photosensitivity, spec §7).
 */
import { Scene } from "@babylonjs/core/scene";
import { Mesh } from "@babylonjs/core/Meshes/mesh";
import { CreateTorus } from "@babylonjs/core/Meshes/Builders/torusBuilder";
import { CreateBox } from "@babylonjs/core/Meshes/Builders/boxBuilder";
import type { StandardMaterial } from "@babylonjs/core/Materials/standardMaterial";
import { Color3 } from "@babylonjs/core/Maths/math.color";
import { makeFlatMaterial } from "./scene";

const POOL = 4;

interface RingSlot {
  mesh: Mesh;
  mat: StandardMaterial;
  age: number;
  durS: number;
  fromScale: number;
  toScale: number;
  active: boolean;
  flat: boolean; // lies in the track plane (landing) vs faces the camera
}

export class ImpactFx {
  private slots: RingSlot[] = [];
  private cpGlow: Mesh;
  private cpGlowMat: StandardMaterial;
  private enabled = true;

  constructor(scene: Scene) {
    for (let i = 0; i < POOL; i++) {
      const mesh = CreateTorus(`fx-ring-${i}`, { diameter: 1, thickness: 0.07, tessellation: 36 }, scene);
      const mat = makeFlatMaterial(scene, `mat-fx-ring-${i}`, "#FFFFFF", 1.2);
      mat.alpha = 0;
      mesh.material = mat;
      mesh.isPickable = false;
      mesh.setEnabled(false);
      this.slots.push({ mesh, mat, age: 0, durS: 0.5, fromScale: 0.3, toScale: 3, active: false, flat: true });
    }
    // the activated checkpoint row stays lit behind the player (VFX-3)
    this.cpGlow = CreateBox("fx-cp-glow", { size: 1 }, scene);
    this.cpGlowMat = makeFlatMaterial(scene, "mat-fx-cp-glow", "#FFFFFF", 1.0);
    this.cpGlowMat.alpha = 0.85;
    this.cpGlow.material = this.cpGlowMat;
    this.cpGlow.isPickable = false;
    this.cpGlow.setEnabled(false);
  }

  setEnabled(on: boolean): void {
    this.enabled = on;
    if (!on) {
      for (const s of this.slots) {
        s.active = false;
        s.mesh.setEnabled(false);
      }
    }
  }

  private fire(
    x: number, y: number, z: number,
    colorHex: string,
    fromScale: number, toScale: number, durS: number,
    flat: boolean,
  ): void {
    if (!this.enabled) return;
    const slot =
      this.slots.find((s) => !s.active) ??
      this.slots.reduce((a, b) => (a.age / a.durS > b.age / b.durS ? a : b));
    slot.active = true;
    slot.age = 0;
    slot.durS = durS;
    slot.fromScale = fromScale;
    slot.toScale = toScale;
    slot.flat = flat;
    slot.mat.emissiveColor = Color3.FromHexString(colorHex).scale(1.2);
    slot.mesh.position.set(x, y, z);
    slot.mesh.rotation.x = flat ? 0 : Math.PI / 2;
    slot.mesh.setEnabled(true);
  }

  /** Landing impact ring (VFX-2) — flat on the surface at the touch point. */
  landing(x: number, y: number, z: number, colorHex: string, heavy: boolean): void {
    this.fire(x, y + 0.06, z, colorHex, 0.4, heavy ? 3.4 : 2.4, heavy ? 0.5 : 0.38, true);
  }

  /** Checkpoint activation (VFX-3): expanding double ring facing the player
   *  + the row's glow slab persists until the next run reset. */
  checkpoint(x: number, z: number, accentHex: string): void {
    this.fire(x, 1.1, z, accentHex, 0.6, 5.2, 0.7, false);
    this.fire(x, 1.1, z, "#FFFFFF", 0.3, 3.4, 0.55, false);
    if (this.enabled) {
      this.cpGlowMat.emissiveColor = Color3.FromHexString(accentHex).scale(1.35);
      this.cpGlow.scaling.set(6.4, 0.1, 0.5);
      this.cpGlow.position.set(0, 0.06, z);
      this.cpGlow.setEnabled(true);
    }
  }

  /** On-beat jump reward (VFX-8): one ring pulse around the ship. */
  beatRing(x: number, y: number, z: number, neonHex: string): void {
    this.fire(x, y + 0.3, z, neonHex, 0.5, 2.0, 0.3, false);
  }

  /** Run reset: the checkpoint slab belongs to the previous attempt. */
  reset(): void {
    this.cpGlow.setEnabled(false);
  }

  /** Advance active rings; cheap no-op when idle. */
  update(dtSeconds: number): void {
    for (const s of this.slots) {
      if (!s.active) continue;
      s.age += dtSeconds;
      const t = Math.min(1, s.age / s.durS);
      const ease = 1 - (1 - t) * (1 - t);
      const scale = s.fromScale + (s.toScale - s.fromScale) * ease;
      s.mesh.scaling.set(scale, s.flat ? 1 : scale, scale);
      s.mat.alpha = 0.9 * (1 - t);
      if (t >= 1) {
        s.active = false;
        s.mesh.setEnabled(false);
      }
    }
  }

  dispose(): void {
    for (const s of this.slots) {
      s.mat.dispose();
      s.mesh.dispose();
    }
    this.cpGlowMat.dispose();
    this.cpGlow.dispose();
  }
}
